import {
	CompareTable,
	InShort,
	Prose,
	RelatedPages,
} from "@marketing/components/answer";
import {
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "What is agentic commerce?",
		a: "Agentic commerce is shopping done by AI agents: software that finds, compares and adds products to a basket for a person, and in some cases completes the purchase, while the person approves. The agent can be a general assistant such as ChatGPT, Gemini or Perplexity, or a store's own assistant in its app, on WhatsApp or on the phone.",
	},
	{
		q: "Can a Gulf grocer sell inside ChatGPT today?",
		a: "We found no grocery retailer selling inside ChatGPT in Saudi Arabia or the UAE as of 5 November 2026. Retailers can build apps inside ChatGPT, as Carrefour did in France and Belgium, and OpenAI takes merchant applications for ChatGPT shopping, but OpenAI has not published a country-by-country opening date.",
	},
	{
		q: "Is ChatGPT Instant Checkout still available?",
		a: "OpenAI launched Instant Checkout on 29 September 2025 for US shoppers. In March 2026 it moved away from it toward retailer apps inside ChatGPT, with purchases completed on the retailer's own site, according to CNBC's reporting as summarised by Retail Insight Network on 23 March 2026.",
	},
	{
		q: "What is the Universal Commerce Protocol?",
		a: "The Universal Commerce Protocol (UCP) is an open standard Google announced in January 2026 for AI agents to discover products and check out with merchants. Google uses it for checkout in AI Mode in Search and in Gemini. Its merchant help page lists eligibility in the United States, Canada and Australia.",
	},
	{
		q: "Is Perplexity shopping available in the Gulf?",
		a: "Perplexity's Instant Buy with PayPal launched for US shoppers in November 2025. We found no report of it opening in Saudi Arabia or the UAE as of 5 November 2026.",
	},
	{
		q: "Does Hephon Commerce list our products in ChatGPT or Gemini?",
		a: "No. Hephon Commerce runs your own assistant in your app, on your WhatsApp number and on calls. It does not publish your catalog into ChatGPT, Gemini or Perplexity today. The catalog work it needs, clean products, pack sizes and stock per branch, is the same work those platforms ask for.",
	},
	{
		q: "Should we wait for ChatGPT or build our own agent now?",
		a: "They are not either-or. Shoppers who already use your app and WhatsApp can order by conversation today with your own agent, with your prices, rules and checkout. If AI platforms open grocery checkout in your market later, a clean, structured catalog lets you join them too.",
	},
];

const CHANNEL_COLUMNS = [
	"",
	"Selling inside ChatGPT, Gemini or Perplexity",
	"Running your own agent (app, WhatsApp, phone)",
];

const CHANNEL_ROWS = [
	[
		"Available to Saudi and UAE grocers today",
		"Not that we found, as of 5 November 2026",
		"Yes",
	],
	[
		"Who reaches the shopper",
		"The AI platform, among other retailers' offers",
		"You, with shoppers who already chose your store",
	],
	[
		"Prices and stock",
		"From a feed or an app you build for the platform",
		"Your live catalog, per branch",
	],
	[
		"Halal and other content rules",
		"Whatever your feed and app enforce",
		"Enforced by the server before the agent sees the catalog",
	],
	[
		"Checkout",
		"The platform's checkout or a link to yours, depending on the programme",
		"Your checkout, confirmed by the shopper",
	],
	[
		"Conversation data",
		"Held by the platform under its own terms",
		"Yours, in your inbox, never used to train a model",
	],
	[
		"Language",
		"The platform's general model",
		"Gulf, Saudi and Lebanese Arabic, Arabizi, English, French, with a phrasebook you approve",
	],
	[
		"Strength",
		"Reach: shoppers who have not found you yet",
		"Repeat orders from your own customers",
	],
];

const KEEP = [
	{
		title: "Branch prices and stock",
		text: "The shopper sees what their branch can deliver, at that branch's price, in your currency.",
	},
	{
		title: "Your content rules",
		text: "Alcohol, pork and tobacco hidden by the server on every channel, failing closed.",
	},
	{
		title: "Your checkout",
		text: "Orders go to your platform or your team, and only after the shopper confirms.",
	},
	{
		title: "Your data",
		text: "Conversations stay in your inbox, and none is used to train an AI model.",
	},
];

export const Route = createFileRoute("/_marketing/agentic-commerce")({
	head: () =>
		marketingHead({
			title: "Agentic Commerce Guide for Gulf Grocers | Hephon Commerce",
			description:
				"Agentic commerce for Gulf grocers: what ChatGPT, Google and Perplexity shopping offer in Saudi Arabia and the UAE today, and what your own agent keeps.",
			path: "/agentic-commerce",
			breadcrumb: ["Agentic commerce"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: AgenticCommercePage,
});

function AgenticCommercePage() {
	return (
		<>
			<PageIntro
				kicker="Agentic commerce"
				title="Agentic commerce: should a Gulf grocer sell inside ChatGPT or run its own agent?"
				lead="What the AI platforms offer merchants today, what is open in Saudi Arabia and the UAE, and what a store's own agent keeps."
			/>

			<InShort updated={UPDATED}>
				<p>
					Agentic commerce is shopping done by AI agents that find
					products, fill a basket and sometimes check out for a
					person. As of 5 November 2026 we found no grocery checkout
					inside ChatGPT, Gemini or Perplexity for shoppers in Saudi
					Arabia or the UAE: Google's lists the United States, Canada
					and Australia, Perplexity's launched in the US, and OpenAI
					moved checkout back to retailers' own sites in March 2026.
				</p>
				<p>
					A Gulf grocer can run its own agent today, in its app, on
					WhatsApp and by phone, and keep its branch prices, halal
					rules, checkout and data. Hephon Commerce is that kind of
					agent.
				</p>
			</InShort>

			<Section id="what-title" title="What is agentic commerce?">
				<Prose>
					<p>
						Agentic commerce means AI agents doing the shopping
						work: reading what a person wants, searching catalogs,
						comparing, filling a basket and, in some setups,
						completing the purchase. The person stays in charge by
						approving the result. "Agentic shopping" is the same
						idea from the shopper's side.
					</p>
					<p>There are two places the agent can live.</p>
					<ul>
						<li>
							<strong>Inside a general AI assistant.</strong>{" "}
							ChatGPT, Google's AI Mode and Gemini, or Perplexity.
							Many stores compete in the same answer, and the
							platform owns the conversation.
						</li>
						<li>
							<strong>Inside the store.</strong> The retailer's
							own app, website, WhatsApp number or phone line. One
							store's catalog, its prices and its rules.
						</li>
					</ul>
					<p>
						To connect the first kind to merchants, the platforms
						have published open protocols: OpenAI's Agentic Commerce
						Protocol and Google's Universal Commerce Protocol. They
						describe how an agent reads a merchant's products,
						builds a checkout and hands payment to the merchant.
					</p>
				</Prose>
			</Section>

			<Section
				id="platforms-title"
				tone="mist"
				title="What can merchants do in ChatGPT, Google and Perplexity today?"
				lead="Each point below names its source and date. We checked them on 5 November 2026."
			>
				<Prose>
					<h3>ChatGPT: from Instant Checkout to retailer apps</h3>
					<p>
						On 29 September 2025 OpenAI announced Instant Checkout
						and the Agentic Commerce Protocol, built with Stripe. At
						launch it let shoppers in the United States buy single
						items from Etsy sellers, with Shopify merchants to
						follow, and merchants kept orders, payments and
						fulfilment in their own systems (OpenAI, "Buy it in
						ChatGPT", 29 September 2025).
					</p>
					<p>
						In March 2026 OpenAI changed course. CNBC reported that
						OpenAI was moving away from Instant Checkout and working
						with retailers on dedicated apps inside ChatGPT, with
						purchases completed on the retailer's own site; Shopify
						confirmed checkout would no longer happen natively in
						ChatGPT (Retail Insight Network, citing CNBC, 23 March
						2026). OpenAI also takes merchant applications for
						ChatGPT shopping at chatgpt.com/merchants, but it has
						not published eligibility rules or a market-by-market
						opening date (Lengow guide to the ChatGPT product feed,
						read 5 November 2026). These are the "ChatGPT merchants"
						and "sell on ChatGPT" programmes people search for.
					</p>
					<h3>
						Carrefour and Mealz: grocery inside ChatGPT in Europe
					</h3>
					<p>
						Grocery reached ChatGPT through apps. In the United
						States, Instacart launched an app inside ChatGPT with
						checkout in the chat for US users (Instacart, 8 December
						2025). In Europe, Carrefour did it first. In France,
						customers can search products, build a basket, get
						recipe and meal-plan suggestions and choose delivery,
						but orders must be finalised on Carrefour's website and
						payment in ChatGPT is not available (Connexion France,
						29 March 2026). Carrefour Belgium followed on 25 August
						2026 with an assistant built on the Mealz.ai shopping
						agent and connected to the retailer's catalog, stock and
						cart, with pickup or home delivery (Mealz, 28 August
						2026).
					</p>
					<h3>Google: the Universal Commerce Protocol</h3>
					<p>
						Google announced the Universal Commerce Protocol in
						January 2026, with checkout on eligible product listings
						in AI Mode in Google Search and in the Gemini app
						(9to5Google, 11 January 2026). Google's merchant help
						page says the checkout applies to products eligible in
						the United States, Canada and Australia, and that the
						merchant stays the merchant of record (Google Merchant
						Center Help, read 5 November 2026).
					</p>
					<h3>Perplexity: Instant Buy with PayPal</h3>
					<p>
						Perplexity and PayPal launched Instant Buy, checkout
						inside Perplexity's chat, for US shoppers in November
						2025 (PYMNTS, 25 November 2025). We found no report of
						it opening in the Gulf.
					</p>
				</Prose>
			</Section>

			<Section
				id="gulf-title"
				title="What is available in Saudi Arabia and the UAE today?"
			>
				<Prose>
					<p>
						For grocery checkout inside a general AI assistant,
						nothing that we could find. As of 5 November 2026, none
						of the sources above lists Saudi Arabia or the UAE, and
						we found no Gulf grocer with an app inside ChatGPT.
					</p>
					<p>
						Apps inside ChatGPT do reach Gulf users in other
						categories. Almosafer, a Riyadh-based travel company,
						launched a ChatGPT app in Arabic and English for hotel
						search in April 2026 (Skift, 13 April 2026). So a Gulf
						retailer could build a ChatGPT app in principle; what is
						missing is checkout and a grocery precedent in the
						region.
					</p>
					<p>
						This will change, and the dates above will age. Treat
						this page as a snapshot with a date on it, and check the
						platforms' own merchant pages before you plan around
						them.
					</p>
				</Prose>
			</Section>

			<Section
				id="choice-title"
				tone="mist"
				title="Sell inside ChatGPT, or run your own agent?"
				lead="They do different jobs. One brings new shoppers; the other serves the ones you have."
			>
				<div className="mx-auto max-w-4xl">
					<CompareTable
						columns={CHANNEL_COLUMNS}
						rows={CHANNEL_ROWS}
						caption="As of 5 November 2026, for a grocer in Saudi Arabia or the UAE."
					/>
				</div>
				<Prose className="mt-14">
					<p>
						A general AI assistant is a discovery channel. Its
						strength is reach: someone who has never opened your app
						can be shown your products. For grocery that matters
						less than for fashion or electronics, because most
						grocery orders are repeat orders from people who already
						have a favourite store and a delivery address with it.
					</p>
					<p>
						Those shoppers are already in your app and on your
						WhatsApp. An agent there lets them order the way they
						talk: a list in one message, "kabsa for 10", "cheapest
						rice", a voice note in Saudi Arabic, a phone call. It
						answers from your catalog alone, with the prices of the
						branch that will deliver.
					</p>
				</Prose>
			</Section>

			<Section
				id="keep-title"
				title="What does your own agent keep?"
				lead="The details a general assistant does not have, and the ones you should not hand over."
			>
				<Steps items={KEEP} />
				<Prose className="mt-14">
					<p>
						Grocery runs on details: a 1 L carton or a 2 L one, the
						price at the branch in Jeddah rather than Riyadh, what
						is in stock in this dark store this hour, a store that
						never sells pork. A general assistant sees a feed; your
						own agent sees the live catalog per branch and your
						rules, enforced on the server before the model sees a
						product.
					</p>
					<p>
						It also keeps the relationship. Every conversation lands
						in your inbox, your team can take over any chat, and the
						shopper pays through your checkout. With Hephon Commerce
						no order is placed without the shopper's explicit
						confirmation, and no conversation is ever used to train
						an AI model.
					</p>
				</Prose>
			</Section>

			<Section
				id="prepare-title"
				tone="mist"
				title="How to prepare for both"
			>
				<Prose>
					<p>
						The same groundwork serves your own agent and any AI
						platform that opens later:
					</p>
					<ol>
						<li>
							<strong>Clean product data.</strong> Names, brands,
							sizes and pack counts in structured fields, in
							Arabic and English.
						</li>
						<li>
							<strong>Prices and stock per branch.</strong> Agents
							can only promise what a branch can deliver.
						</li>
						<li>
							<strong>A product feed or API.</strong> Hephon
							Commerce reads Shopify, WooCommerce, product feeds
							including the Google product feed format and JSON
							lines, and custom connectors. The platforms ask for
							feeds too.
						</li>
						<li>
							<strong>Clear rules.</strong> Which products you
							never sell, when a person takes over, how
							substitutions work.
						</li>
						<li>
							<strong>
								A checkout that can take a prepared basket.
							</strong>{" "}
							An API or order ticket your team already handles.
						</li>
					</ol>
				</Prose>
			</Section>

			<Section id="fit-title" title="What Hephon Commerce does not do">
				<Prose>
					<ul>
						<li>
							It does not list your products in ChatGPT, Gemini or
							Perplexity, and it does not implement the Agentic
							Commerce Protocol or the Universal Commerce Protocol
							today.
						</li>
						<li>
							It does not bring you new shoppers from AI
							platforms. It serves shoppers who reach you in your
							app, on WhatsApp and by phone.
						</li>
						<li>
							It does not take payment on its own. Checkout is
							yours, and the shopper confirms every order.
						</li>
						<li>
							It is not open for self-serve sign-up. Access is by
							invitation, after a demo on your catalog.
						</li>
					</ul>
				</Prose>
			</Section>

			<Faq items={FAQ} title="Agentic commerce, answered." />
			<RelatedPages
				links={[
					{
						href: "/ai-agent-for-ecommerce",
						label: "Ecommerce AI agent",
					},
					{
						href: "/how-it-works",
						label: "How the shopping agent works",
					},
					{ href: "/channels/whatsapp", label: "WhatsApp shopping" },
					{ href: "/supermarkets", label: "AI for supermarkets" },
					{ href: "/saudi-arabia", label: "Saudi Arabia" },
					{ href: "/uae", label: "UAE" },
				]}
			/>
			<Closing />
		</>
	);
}
